import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Download, Loader2, Megaphone, ArrowLeft } from 'lucide-react'
import { format } from 'date-fns'
import { ReportFilters, getDateRange } from '@/components/ReportFilters'
import type { ReportFilterValues } from '@/components/ReportFilters'
import { formatCurrency } from '@/lib/utils'
import { exportToCSV } from '@/utils/csvExport'
import { useMarketingSingleMonth, useMarketingByMonth } from '@/hooks/use-api'
import type { MarketingSourceRow } from '@/types'
import { explainError } from '@/lib/explain-error'

type Mode = 'single' | 'range'
type RangeStatus = 'pipeline' | 'won'
type RangeMetric = 'count' | 'value'

function currentMonth(): string {
  return format(new Date(), 'yyyy-MM')
}

function monthLabel(ym: string): string {
  // ym is YYYY-MM; anchor at local midnight so the month never shifts.
  return format(new Date(`${ym}-01T00:00:00`), 'MMM yyyy')
}

/** Announce an action to assistive tech via a transient polite live region. */
function announce(message: string) {
  const el = document.createElement('div')
  el.setAttribute('role', 'status')
  el.setAttribute('aria-live', 'polite')
  el.className = 'sr-only'
  el.textContent = message
  document.body.appendChild(el)
  window.setTimeout(() => document.body.removeChild(el), 1000)
}

/** A single Source | Count | Value table with a TOTAL row (Reports 1 & 2). */
function SourceValueTable({
  caption,
  rows,
  totals,
}: {
  caption: string
  rows: MarketingSourceRow[]
  totals: { count: number; value: number }
}) {
  return (
    <Table aria-label={caption}>
      <TableHeader>
        <TableRow>
          <TableHead>Lead Source</TableHead>
          <TableHead className="text-right">Opportunity Count</TableHead>
          <TableHead className="text-right">Value</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={3} className="text-center text-muted-foreground py-6">
              No marketing-generated opportunities for this month.
            </TableCell>
          </TableRow>
        ) : (
          rows.map((row) => (
            <TableRow key={row.lead_source}>
              <TableCell>{row.lead_source}</TableCell>
              <TableCell className="text-right tabular-nums">
                {row.count.toLocaleString()}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatCurrency(row.value, 'CAD')}
              </TableCell>
            </TableRow>
          ))
        )}
        <TableRow className="border-t-2 font-semibold">
          <TableCell>TOTAL</TableCell>
          <TableCell className="text-right tabular-nums">{totals.count.toLocaleString()}</TableCell>
          <TableCell className="text-right tabular-nums">
            {formatCurrency(totals.value, 'CAD')}
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  )
}

export default function MarketingReports() {
  const navigate = useNavigate()
  const [mode, setMode] = useState<Mode>('single')

  // Owner scope is shared across modes; date range only applies to range mode.
  const [filters, setFilters] = useState<ReportFilterValues>(() => getDateRange('fiscal_year'))
  const [month, setMonth] = useState<string>(currentMonth)
  const [status, setStatus] = useState<RangeStatus>('pipeline')
  const [metric, setMetric] = useState<RangeMetric>('count')

  const singleMonth = useMarketingSingleMonth({ month, owner_id: filters.owner_id })
  const byMonth = useMarketingByMonth({
    startDate: filters.startDate,
    endDate: filters.endDate,
    status,
    owner_id: filters.owner_id,
  })

  // Pivot the flat by-month rows into a source × month matrix for the selected metric.
  const pivot = useMemo(() => {
    const data = byMonth.data
    if (!data) return null
    const { months, rows } = data
    const sources = Array.from(new Set(rows.map((r) => r.lead_source))).sort((a, b) =>
      a.localeCompare(b)
    )
    // cell[source][month] = metric value
    const cell = new Map<string, Map<string, number>>()
    for (const r of rows) {
      if (!cell.has(r.lead_source)) cell.set(r.lead_source, new Map())
      cell.get(r.lead_source)!.set(r.month, metric === 'count' ? r.count : r.value)
    }
    const get = (source: string, m: string) => cell.get(source)?.get(m) ?? 0
    const sourceRows = sources.map((source) => {
      const cells = months.map((m) => get(source, m))
      const total = cells.reduce((a, b) => a + b, 0)
      return { source, cells, total }
    })
    const monthTotals = months.map((m) => sources.reduce((sum, source) => sum + get(source, m), 0))
    const grandTotal = monthTotals.reduce((a, b) => a + b, 0)
    return { months, sourceRows, monthTotals, grandTotal }
  }, [byMonth.data, metric])

  const fmt = (n: number) => (metric === 'value' ? formatCurrency(n, 'CAD') : n.toLocaleString())

  const handleExportSingle = () => {
    const data = singleMonth.data
    if (!data) return
    const records = [
      ...data.pipeline.map((r) => ({ section: 'Pipeline (Open)', ...r })),
      ...data.revenue.map((r) => ({ section: 'Revenue (Closed/Won)', ...r })),
    ]
    exportToCSV({
      filename: `Marketing_${data.month}`,
      data: records,
      columns: ['section', 'lead_source', 'count', 'value'],
      headers: ['Report', 'Lead Source', 'Opportunity Count', 'Value (CAD)'],
    })
    announce(`Exported marketing report for ${monthLabel(data.month)} to CSV.`)
  }

  const handleExportRange = () => {
    if (!pivot) return
    const records = pivot.sourceRows.map((row) => {
      const rec: Record<string, string | number> = { lead_source: row.source }
      pivot.months.forEach((m, i) => {
        rec[m] = row.cells[i] ?? 0
      })
      rec.total = row.total
      return rec
    })
    exportToCSV({
      filename: `Marketing_${status}_${metric}_${filters.startDate ?? 'start'}_${filters.endDate ?? 'end'}`,
      data: records,
      columns: ['lead_source', ...pivot.months, 'total'],
      headers: ['Lead Source', ...pivot.months.map(monthLabel), 'Total'],
    })
    announce('Exported marketing trend report to CSV.')
  }

  const isLoading = mode === 'single' ? singleMonth.isLoading : byMonth.isLoading
  const error = mode === 'single' ? singleMonth.error : byMonth.error

  return (
    <main className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/reports')}
            aria-label="Back to reports"
          >
            <ArrowLeft className="h-5 w-5" aria-hidden="true" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <Megaphone className="h-5 w-5 text-[#183E6A]" aria-hidden="true" />
              <h1 className="text-2xl font-bold">Marketing Reports</h1>
              <Badge variant="secondary">Pre-built</Badge>
            </div>
            <p className="text-muted-foreground mt-1">
              Marketing-generated pipeline and revenue by lead source, anchored on opportunity
              created date.
            </p>
          </div>
        </div>
      </div>

      {/* Mode toggle */}
      <div className="inline-flex rounded-md border p-0.5" role="tablist" aria-label="Report view">
        <Button
          role="tab"
          aria-selected={mode === 'single'}
          variant={mode === 'single' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setMode('single')}
        >
          Single Month
        </Button>
        <Button
          role="tab"
          aria-selected={mode === 'range'}
          variant={mode === 'range' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setMode('range')}
        >
          Date Range
        </Button>
      </div>

      {/* Controls */}
      <div className="flex items-end gap-3 flex-wrap">
        {mode === 'single' ? (
          <>
            <div>
              <Label htmlFor="marketing-month" className="text-xs text-muted-foreground mb-1 block">
                Month
              </Label>
              <input
                id="marketing-month"
                type="month"
                value={month}
                max={currentMonth()}
                onChange={(e) => setMonth(e.target.value || currentMonth())}
                className="h-9 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
            <ReportFilters value={filters} onChange={setFilters} showPeriod={false} />
            <Button
              variant="outline"
              onClick={handleExportSingle}
              disabled={!singleMonth.data}
              aria-label="Export single-month report to CSV"
            >
              <Download className="mr-2 h-4 w-4" aria-hidden="true" />
              CSV
            </Button>
          </>
        ) : (
          <>
            <div>
              <Label
                htmlFor="marketing-status"
                className="text-xs text-muted-foreground mb-1 block"
              >
                Status
              </Label>
              <Select value={status} onValueChange={(v) => setStatus(v as RangeStatus)}>
                <SelectTrigger id="marketing-status" className="w-[160px] h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pipeline">Pipeline (Full Funnel)</SelectItem>
                  <SelectItem value="won">Closed/Won</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label
                htmlFor="marketing-metric"
                className="text-xs text-muted-foreground mb-1 block"
              >
                Metric
              </Label>
              <Select value={metric} onValueChange={(v) => setMetric(v as RangeMetric)}>
                <SelectTrigger id="marketing-metric" className="w-[140px] h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="count">Count</SelectItem>
                  <SelectItem value="value">Value</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <ReportFilters
              value={filters}
              onChange={setFilters}
              defaultPeriod="fiscal_year"
              datePresets={[
                'this_quarter',
                'fiscal_year',
                'calendar_year',
                'last_12_months',
                'custom',
              ]}
            />
            <Button
              variant="outline"
              onClick={handleExportRange}
              disabled={!pivot || pivot.sourceRows.length === 0}
              aria-label="Export trend report to CSV"
            >
              <Download className="mr-2 h-4 w-4" aria-hidden="true" />
              CSV
            </Button>
          </>
        )}
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading" />
        </div>
      )}

      {error && (
        <Card>
          <CardContent className="py-6 text-destructive">
            Failed to load report: {explainError(error, 'Unknown error')}
          </CardContent>
        </Card>
      )}

      {/* Single-month view: Reports 1 & 2 */}
      {mode === 'single' && !isLoading && singleMonth.data && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Marketing-Generated Pipeline</CardTitle>
              <CardDescription>
                All marketing-generated opportunities created in{' '}
                {monthLabel(singleMonth.data.month)} — the full funnel (open, won, and lost).
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SourceValueTable
                caption="Marketing-generated pipeline by lead source"
                rows={singleMonth.data.pipeline}
                totals={singleMonth.data.pipelineTotals}
              />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Marketing-Generated Revenue (Closed/Won)</CardTitle>
              <CardDescription>
                Closed/won opportunities created in {monthLabel(singleMonth.data.month)} (filtered
                on created date, not close date).
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SourceValueTable
                caption="Marketing-generated revenue by lead source"
                rows={singleMonth.data.revenue}
                totals={singleMonth.data.revenueTotals}
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Date-range view: Reports 3-6 (source × month matrix) */}
      {mode === 'range' && !isLoading && pivot && (
        <Card>
          <CardHeader>
            <CardTitle>
              {status === 'won' ? 'Closed/Won' : 'Pipeline (Full Funnel)'} by Month —{' '}
              {metric === 'value' ? 'Value' : 'Opportunity Count'}
            </CardTitle>
            <CardDescription>
              Marketing-generated opportunities by lead source and month created.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table aria-label="Marketing opportunities by source and month">
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky left-0 bg-background z-10">Lead Source</TableHead>
                    {pivot.months.map((m) => (
                      <TableHead key={m} className="text-right whitespace-nowrap">
                        {monthLabel(m)}
                      </TableHead>
                    ))}
                    <TableHead className="text-right font-semibold">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pivot.sourceRows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={pivot.months.length + 2}
                        className="text-center text-muted-foreground py-6"
                      >
                        No marketing-generated opportunities in this range.
                      </TableCell>
                    </TableRow>
                  ) : (
                    pivot.sourceRows.map((row) => (
                      <TableRow key={row.source}>
                        <TableCell className="sticky left-0 bg-background z-10 font-medium">
                          {row.source}
                        </TableCell>
                        {row.cells.map((c, i) => (
                          <TableCell key={pivot.months[i]} className="text-right tabular-nums">
                            {fmt(c)}
                          </TableCell>
                        ))}
                        <TableCell className="text-right tabular-nums font-semibold">
                          {fmt(row.total)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                  <TableRow className="border-t-2 font-semibold">
                    <TableCell className="sticky left-0 bg-background z-10">TOTAL</TableCell>
                    {pivot.monthTotals.map((t, i) => (
                      <TableCell key={pivot.months[i]} className="text-right tabular-nums">
                        {fmt(t)}
                      </TableCell>
                    ))}
                    <TableCell className="text-right tabular-nums">
                      {fmt(pivot.grandTotal)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </main>
  )
}
